/**
 * Learning Center API: campaigns, the Assign wizard's send and its history
 * (plan docs/plans/lms-plan.md Appendix A "Assignments and campaigns"; 5.4,
 * 8.2, 18.16, 18.21; WP-31's routes, WP-43's client). Typed calls over
 * services/api; nothing under src/lms imports axios (2.3a). Every route is an
 * lms admin route.
 *
 *   POST /admin/campaigns/preview {course_id, audience, course_version_id?, force?, exclude_user_ids?}
 *        -> { recipients: [{ user_id, full_name, email, outcome_if_sent }], count }   (writes nothing)
 *   POST /admin/campaigns {course_id, course_version_id?, kind, audience, due_days, message?, force?, exclude_user_ids?}
 *        -> 201 { campaign, outcomes: { assigned, already_assigned, already_current, skipped_inactive },
 *                 notifications_failed }
 *   GET  /admin/campaigns?course_id=&kind=&limit=&offset= -> { items, page }
 *   GET  /admin/campaigns/:id -> { campaign, recipients: [{ user_id, full_name, outcome, assignment }] }
 *
 * The audience (5.4, 18.16) is the four selectors as the person chose them,
 * combined as a UNION on the server: everyone at these sites, these platform
 * roles AT THE ACTING SITE (X-Site-Id), the active members of these groups,
 * these people. exclude_user_ids is the wizard's untick: the stored audience
 * stays as selected and the excluded people get no recipient row. A site,
 * group or person outside the admin's reach is 404 (G14, 4.9). The wizard
 * sends kind manual, scenario or sop_update only (retrain and acknowledge
 * campaigns come from a publish, 8.1). In the list and the detail,
 * audience.user_ids holds only the people the viewing admin reaches; the rest
 * are counted in audience_hidden_user_count (18.21).
 */
import api from '../../services/api';
import type { LmsList, LmsPlatformRole } from './client';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Bounds (services/lms/assignments/campaigns.js)
// ---------------------------------------------------------------------------

/** 8.2: a campaign's due window in whole days, 1 to 365. */
export const CAMPAIGN_DUE_DAYS_MIN = 1;
export const CAMPAIGN_DUE_DAYS_MAX = 365;
/** 5.8 default_due_days (the network setting's default; the wizard pre-fills it). */
export const DEFAULT_CAMPAIGN_DUE_DAYS = 30;
/** 5.4: the message shown in the notification and on the assignment card. */
export const CAMPAIGN_MESSAGE_MAX = 2000;
/** Appendix A: a list page holds at most 200 rows. */
export const CAMPAIGNS_MAX_LIMIT = 200;

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** 5.4 lms_campaigns.kind. */
export type CampaignKind = 'manual' | 'sop_update' | 'scenario' | 'retrain' | 'acknowledge';
export const CAMPAIGN_KINDS: readonly CampaignKind[] = ['manual', 'sop_update', 'scenario', 'retrain', 'acknowledge'];

/** The kinds a person sends from the wizard (18.16). */
export type WizardCampaignKind = 'manual' | 'scenario' | 'sop_update';

/** 5.4 lms_campaigns.audience, as the person selected it (the union of four selectors). */
export interface CampaignAudience {
  everyone_at_sites: string[];
  /** Resolved at the acting site (18.16). */
  platform_roles: LmsPlatformRole[];
  group_ids: string[];
  user_ids: string[];
}

/** A fresh audience with no selector set (new arrays every call, so nothing shares state). */
export function emptyAudience(): CampaignAudience {
  return { everyone_at_sites: [], platform_roles: [], group_ids: [], user_ids: [] };
}

/** True when no selector names anyone (the server answers 400 for such an audience). */
export function audienceIsEmpty(a: CampaignAudience | null | undefined): boolean {
  if (!a) return true;
  return (
    (a.everyone_at_sites?.length ?? 0) === 0 &&
    (a.platform_roles?.length ?? 0) === 0 &&
    (a.group_ids?.length ?? 0) === 0 &&
    (a.user_ids?.length ?? 0) === 0
  );
}

/** 5.4 lms_campaign_recipients.outcome (the rule of 8.2 step 4). */
export type CampaignOutcome = 'assigned' | 'already_assigned' | 'already_current' | 'skipped_inactive';
export const CAMPAIGN_OUTCOMES: readonly CampaignOutcome[] = ['assigned', 'already_assigned', 'already_current', 'skipped_inactive'];

export type CampaignOutcomeCounts = Record<CampaignOutcome, number>;

/** One person in POST /admin/campaigns/preview. */
export interface PreviewRecipient {
  user_id: string;
  full_name: string | null;
  email: string | null;
  outcome_if_sent: CampaignOutcome;
}

export interface CampaignPreview {
  recipients: PreviewRecipient[];
  count: number;
}

export interface PreviewCampaignInput {
  course_id: string;
  audience: CampaignAudience;
  course_version_id?: string | null;
  force?: boolean;
  exclude_user_ids?: string[];
}

export interface SendCampaignInput {
  course_id: string;
  /** A pinned (published) version, or omitted for "current when the person starts" (5.4). */
  course_version_id?: string | null;
  kind: WizardCampaignKind;
  audience: CampaignAudience;
  due_days: number;
  message?: string | null;
  /** 8.2: also send to people who already hold a current completion. */
  force?: boolean;
  exclude_user_ids?: string[];
}

/** An lms_campaigns row (5.4). */
export interface LmsCampaign {
  id: string;
  network_id: string;
  site_id: string | null;
  course_id: string;
  course_version_id: string | null;
  kind: CampaignKind;
  message: string | null;
  due_days: number;
  due_at: string;
  audience: CampaignAudience | null;
  force: boolean;
  recipient_count: number;
  sent_by: string;
  sent_at: string;
  created_at: string;
}

/** One GET /admin/campaigns item, and the detail's campaign (18.21). */
export interface CampaignListItem extends LmsCampaign {
  /** People named in the audience whom the viewing admin does not reach: counted, never named (G14). */
  audience_hidden_user_count: number;
  course: { id: string; title: string; slug: string; category: string } | null;
  sent_by_name: string | null;
}

export interface SendCampaignResult {
  campaign: LmsCampaign;
  outcomes: CampaignOutcomeCounts;
  /** Notices that could not be written; the assignments stand (18.16). */
  notifications_failed: number;
}

/** 5.4 lms_assignments.status. */
export type AssignmentStatus = 'open' | 'completed' | 'waived' | 'cancelled';

/** One person in GET /admin/campaigns/:id (the people the viewing admin reaches). */
export interface CampaignRecipient {
  user_id: string;
  full_name: string | null;
  outcome: CampaignOutcome;
  assignment: { id: string; status: AssignmentStatus; completed_at: string | null; due_at: string } | null;
}

export interface CampaignDetail {
  campaign: CampaignListItem;
  recipients: CampaignRecipient[];
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** POST /api/lms/admin/campaigns/preview: who a send would reach and each outcome, writing nothing. */
export async function previewCampaign(input: PreviewCampaignInput): Promise<CampaignPreview> {
  const body: Record<string, unknown> = { course_id: input.course_id, audience: input.audience };
  if (input.course_version_id) body.course_version_id = input.course_version_id;
  if (input.force !== undefined) body.force = input.force;
  if (input.exclude_user_ids && input.exclude_user_ids.length > 0) body.exclude_user_ids = input.exclude_user_ids;
  const response = await api.post<CampaignPreview>('/api/lms/admin/campaigns/preview', body);
  return response.data;
}

/**
 * POST /api/lms/admin/campaigns: the body of Appendix A (plus exclude_user_ids,
 * 18.16). course_version_id and message are sent only when set; force and
 * exclude_user_ids always.
 */
export async function sendCampaign(input: SendCampaignInput): Promise<SendCampaignResult> {
  const body: Record<string, unknown> = {
    course_id: input.course_id,
    kind: input.kind,
    audience: input.audience,
    due_days: input.due_days,
    force: input.force === true,
    exclude_user_ids: input.exclude_user_ids ?? [],
  };
  if (input.course_version_id) body.course_version_id = input.course_version_id;
  const message = (input.message ?? '').trim();
  if (message) body.message = message;
  const response = await api.post<SendCampaignResult>('/api/lms/admin/campaigns', body);
  return response.data;
}

export interface CampaignListQuery {
  courseId?: string | null;
  kind?: CampaignKind | null;
  limit?: number;
  offset?: number;
}

/** GET /api/lms/admin/campaigns (newest first). */
export async function listCampaigns(query: CampaignListQuery = {}): Promise<LmsList<CampaignListItem>> {
  const params: Record<string, string | number> = {};
  if (query.courseId) params.course_id = query.courseId;
  if (query.kind) params.kind = query.kind;
  if (query.limit !== undefined) params.limit = Math.min(query.limit, CAMPAIGNS_MAX_LIMIT);
  if (query.offset !== undefined) params.offset = query.offset;
  const response = await api.get<LmsList<CampaignListItem>>('/api/lms/admin/campaigns', { params });
  return response.data;
}

/** GET /api/lms/admin/campaigns/:id */
export async function getCampaign(campaignId: string): Promise<CampaignDetail> {
  const response = await api.get<CampaignDetail>(`/api/lms/admin/campaigns/${enc(campaignId)}`);
  return response.data;
}
